import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "@/lib/brand";

const navLinks = [
  { href: "/quote", label: "Instant Quote", id: "nav-quote" },
  { href: "/#services", label: "Services", id: "nav-services" },
  { href: "/#why", label: "Why Us", id: "nav-why" },
  { href: "/#coverage", label: "Coverage", id: "nav-coverage" },
  { href: "/blog", label: "Blog", id: "nav-blog" },
  { href: "/#contact", label: "Contact", id: "nav-contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/95 border-b border-white/10"
          : "bg-black/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="header-logo" className="flex items-center gap-3 group">
          <img
            src={BRAND.logoUrl}
            alt="MHJ Prestige Transport"
            className="h-12 w-12 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-extrabold text-white text-base tracking-tight">
              MHJ PRESTIGE
            </span>
            <span className="text-[10px] tracking-[0.3em] text-zinc-400 uppercase mt-1">
              Transport · Recovery
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#E60000] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/quote"
            data-testid="header-call-button"
            className="hidden md:inline-flex items-center gap-2 px-5 py-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold text-sm tracking-wide transition-colors"
          >
            Instant Quote
          </a>
          <button
            data-testid="header-mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 border border-white/10"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.id}`}
                className="text-zinc-200 hover:text-white text-lg font-medium"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-4 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold mt-2"
              data-testid="mobile-call-button"
            >
              <Phone className="w-4 h-4" />
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
